import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useJarvis } from '../../hooks/useJarvis';
import {
  getJarvisMemoryData, getTrades, getDailyLessons,
  getRules, getBehaviors, getPatterns, getKnowledgeChunks,
  getPsychologyLogs, getPredictions, saveRule,
} from '../../services/storage';
import {
  getBrainReport, getStrategies, getHypotheses,
  STRATEGY_STATUS, HYP_STATUS,
} from '../../engines/jarvisBrain';
import { computePatterns, MIN_SAMPLE_SIZE } from '../../engines/selfLearning';

const C = {
  bg: '#0a0e1a', card: '#111827', border: '#1e293b',
  text: '#e2e8f0', muted: '#94a3b8',
  green: '#00ff88', red: '#ff3333', gold: '#ffd700',
  blue: '#00d4ff', purple: '#a78bfa', orange: '#fb923c',
};

const card  = (extra) => ({ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px', ...extra });
const btn   = (col) => ({
  background: `${col}18`, border: `1px solid ${col}44`,
  borderRadius: '7px', padding: '8px 14px', color: col,
  fontSize: '12px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: '600',
});
const tabBtn = (active, col = C.blue) => ({
  background: active ? `${col}18` : 'rgba(255,255,255,0.03)',
  border: `1px solid ${active ? col : C.border}`,
  borderRadius: '6px', padding: '7px 8px', color: active ? col : C.muted,
  fontSize: '11px', cursor: 'pointer', fontFamily: 'monospace', flex: 1,
});

// ── Stat pill ──────────────────────────────────────────────────────────────
function Stat({ label, value, color, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '10px 6px' }}>
      <div style={{ fontSize: '24px', fontWeight: '800', color: color || C.blue, fontFamily: 'monospace', lineHeight: 1 }}>
        {value ?? 0}
      </div>
      <div style={{ fontSize: '10px', color: C.muted, marginTop: '3px', letterSpacing: '0.5px' }}>{label}</div>
      {sub && <div style={{ fontSize: '9px', color: color || C.blue, marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

// ── Complete Memory Overview ──────────────────────────────────────────────
function MemoryOverview() {
  const rules     = getRules();
  const behaviors = getBehaviors();
  const patterns  = getPatterns();
  const chunks    = getKnowledgeChunks();
  const psychLogs = getPsychologyLogs();
  const preds     = getPredictions();
  const trades    = getTrades();
  const strategies = getStrategies();
  const memory    = getJarvisMemoryData();

  const activeRules   = rules.filter(r => r.status === 'active').length;
  const confirmedBeh  = behaviors.filter(b => ['HIGH','CONFIRMED'].includes(b.confidence)).length;
  const strongPat     = patterns.filter(p => ['STRONG','CONFIRMED'].includes(p.confidence)).length;
  const provenStrats  = strategies.filter(s => [STRATEGY_STATUS.PROVEN, STRATEGY_STATUS.STRONG].includes(s.status)).length;
  const closedTrades  = trades.filter(t => t.result && t.result !== 'OPEN').length;
  const correctPreds  = preds.filter(p => p.was_correct === true).length;

  // Chunks by category
  const chunksByCategory = chunks.reduce((acc, c) => {
    acc[c.category] = (acc[c.category] || 0) + 1;
    return acc;
  }, {});

  const sections = [
    {
      title: '📚 KNOWLEDGE BASE',
      color: C.blue,
      items: [
        { label: 'Total Chunks',   value: chunks.length,                   color: C.blue   },
        { label: 'Rules/Checklist',value: (chunksByCategory.rule || 0) + (chunksByCategory.checklist || 0), color: C.green  },
        { label: 'Concepts',       value: chunksByCategory.concept || 0,   color: C.gold   },
        { label: 'Strategies',     value: chunksByCategory.strategy || 0,  color: C.purple },
        { label: 'Mistakes',       value: chunksByCategory.mistake || 0,   color: C.orange },
        { label: 'Examples',       value: chunksByCategory.example || 0,   color: C.muted  },
      ],
    },
    {
      title: '📋 RULE BOOK',
      color: C.green,
      items: [
        { label: 'Total Rules',    value: rules.length,      color: C.blue  },
        { label: 'Active Rules',   value: activeRules,       color: C.green },
        { label: 'Auto-Extracted', value: rules.filter(r => r.auto_extracted).length, color: C.gold },
        { label: 'Win Rate >60%',  value: rules.filter(r => r.win_rate >= 60 && r.total_count >= 3).length, color: C.green },
      ],
    },
    {
      title: '🧠 BEHAVIORS & PATTERNS',
      color: C.gold,
      items: [
        { label: 'Behaviors',      value: behaviors.length,   color: C.blue   },
        { label: 'Confirmed Beh',  value: confirmedBeh,       color: C.green  },
        { label: 'Patterns',       value: patterns.length,    color: C.gold   },
        { label: 'Strong Patterns',value: strongPat,          color: C.green  },
      ],
    },
    {
      title: '🎯 STRATEGIES & BRAIN',
      color: C.purple,
      items: [
        { label: 'Total Strategies',value: strategies.length,   color: C.blue   },
        { label: 'Proven/Strong',   value: provenStrats,        color: C.green  },
        { label: 'Hypotheses',      value: getHypotheses().length, color: C.gold },
        { label: 'Confirmed Hyp',   value: getHypotheses().filter(h => h.confidence === HYP_STATUS.CONFIRMED).length, color: C.green },
      ],
    },
    {
      title: '📊 TRADING RECORD',
      color: C.orange,
      items: [
        { label: 'Total Trades',   value: trades.length,        color: C.blue   },
        { label: 'Closed Trades',  value: closedTrades,         color: C.green  },
        { label: 'Predictions',    value: preds.length,         color: C.gold   },
        { label: 'Correct Preds',  value: correctPreds,         color: C.green  },
        { label: 'Psychology Logs',value: psychLogs.length,     color: C.purple },
        { label: 'Daily Lessons',  value: getDailyLessons().length, color: C.blue },
      ],
    },
    {
      title: '💬 JARVIS ACTIVITY',
      color: C.blue,
      items: [
        { label: 'Discussions',    value: memory.total_discussions || 0,  color: C.blue   },
        { label: 'Screenshots',    value: memory.total_screenshots || 0,  color: C.purple },
        { label: 'Knowledge Score',value: memory.knowledge_score || 0,    color: C.gold   },
        { label: 'Self-Gen Strats',value: memory.self_generated_strategies || 0, color: C.green },
      ],
    },
  ];

  return (
    <div>
      {sections.map(sec => (
        <div key={sec.title} style={card()}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: sec.color, letterSpacing: '2px', marginBottom: '10px' }}>
            {sec.title}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
            {sec.items.map(item => (
              <Stat key={item.label} label={item.label} value={item.value} color={item.color} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Brain + Evolution Tab ──────────────────────────────────────────────────
const STAGE_CONFIG = [
  { stage: 1, label: 'NEWBORN',     color: C.muted,  minScore: 0   },
  { stage: 2, label: 'LEARNING',    color: C.blue,   minScore: 20  },
  { stage: 3, label: 'DEVELOPING',  color: C.gold,   minScore: 50  },
  { stage: 4, label: 'INTELLIGENT', color: C.purple, minScore: 100 },
  { stage: 5, label: 'EVOLVED',     color: C.green,  minScore: 200 },
];

function BrainTab({ memory, report, onRunBrain, isRunning, brainResult }) {
  const score = memory.knowledge_score || 0;
  const stage = STAGE_CONFIG.slice().reverse().find(s => score >= s.minScore) || STAGE_CONFIG[0];
  const nextStage = STAGE_CONFIG[STAGE_CONFIG.indexOf(stage) + 1];
  const pct = nextStage
    ? Math.min(((score - stage.minScore) / (nextStage.minScore - stage.minScore)) * 100, 100)
    : 100;

  return (
    <>
      {/* Evolution meter */}
      <div style={{ ...card({ background: `${stage.color}08`, borderColor: `${stage.color}33` }) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '10px', color: C.muted, marginBottom: '2px' }}>EVOLUTION STAGE {stage.stage}/5</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: stage.color, letterSpacing: '2px' }}>{stage.label}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '28px', fontWeight: '800', color: C.gold, fontFamily: 'monospace' }}>{score}</div>
            <div style={{ fontSize: '10px', color: C.muted }}>knowledge score</div>
          </div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: stage.color, borderRadius: '4px', transition: 'width 0.4s' }} />
        </div>
        {nextStage && (
          <div style={{ fontSize: '10px', color: C.muted, marginTop: '4px' }}>
            {Math.round(pct)}% to Stage {stage.stage + 1} ({stage.label} → {nextStage.label}) — need {nextStage.minScore - score} more
          </div>
        )}
      </div>

      {/* Brain result */}
      {brainResult && (
        <div style={{ ...card({ background: 'rgba(167,139,250,0.08)' }), fontSize: '12px', color: C.purple }}>
          🧠 Brain done — {brainResult.newStrategies?.length || 0} new strategies, {brainResult.newHypotheses?.length || 0} new hypotheses, {brainResult.evolution?.evolved || 0} evolved
        </div>
      )}

      <button style={{ ...btn(C.purple), width: '100%', padding: '12px', fontSize: '13px', marginBottom: '12px', opacity: isRunning ? 0.6 : 1 }}
        onClick={onRunBrain} disabled={isRunning}>
        {isRunning ? '🧠 Analysing...' : '🧠 Run JARVIS Brain Analysis'}
      </button>

      {/* Stats grid */}
      <div style={{ ...card(), display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
        {[
          { label: 'Strategies',    value: report.strategies.total,    color: C.gold   },
          { label: 'Proven',        value: report.strategies.proven,   color: C.green  },
          { label: 'Hypotheses',    value: report.hypotheses.total,    color: C.orange },
          { label: 'Confirmed Hyp', value: report.hypotheses.confirmed,color: C.green  },
          { label: 'Discussions',   value: memory.total_discussions || 0, color: C.blue },
          { label: 'Screenshots',   value: memory.total_screenshots || 0, color: C.purple },
        ].map(s => <Stat key={s.label} {...s} />)}
      </div>

      {/* Strengths */}
      <div style={card()}>
        <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '2px', marginBottom: '8px' }}>STRENGTHS & WEAKNESSES</div>
        <div style={{ fontSize: '12px', color: C.green, marginBottom: '6px' }}>✅ {report.strengths.summary}</div>
        <div style={{ fontSize: '12px', color: C.orange }}>⚠️ {report.weaknesses.summary}</div>
      </div>
    </>
  );
}

// ── Strategies Tab ─────────────────────────────────────────────────────────
const sbadge = (s) => {
  const map = {
    [STRATEGY_STATUS.HYPOTHESIS]: { bg: 'rgba(100,116,139,0.3)', col: C.muted,   label: 'HYPOTHESIS' },
    [STRATEGY_STATUS.TESTING]:    { bg: 'rgba(0,212,255,0.2)',   col: C.blue,    label: 'TESTING'    },
    [STRATEGY_STATUS.PROVEN]:     { bg: 'rgba(0,255,136,0.2)',   col: C.green,   label: 'PROVEN ✓'   },
    [STRATEGY_STATUS.STRONG]:     { bg: 'rgba(0,255,136,0.3)',   col: C.green,   label: 'STRONG ⭐'  },
    [STRATEGY_STATUS.RETIRED]:    { bg: 'rgba(255,51,51,0.15)',  col: C.red,     label: 'RETIRED'    },
  };
  const c = map[s] || map[STRATEGY_STATUS.HYPOTHESIS];
  return <span style={{ background: c.bg, color: c.col, borderRadius: '4px', padding: '2px 7px', fontSize: '10px', fontWeight: '700' }}>{c.label}</span>;
};

function StrategiesTab({ strategies }) {
  const [filter, setFilter] = useState('all');
  const filters = ['all', STRATEGY_STATUS.PROVEN, STRATEGY_STATUS.STRONG, STRATEGY_STATUS.TESTING, STRATEGY_STATUS.HYPOTHESIS];
  const shown = filter === 'all' ? strategies : strategies.filter(s => s.status === filter);

  if (!strategies.length) {
    return (
      <div style={{ ...card(), textAlign: 'center', padding: '30px', color: C.muted, fontSize: '13px' }}>
        <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎯</div>
        <div>Strategies 0 आहेत कारण अजून 5+ closed trades नाहीत.</div>
        <div style={{ marginTop: '8px', fontSize: '11px', color: C.blue }}>
          Knowledge Base मध्ये "strategy" category chunks असल्यास ते auto-sync होतील.
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {filters.map(f => (
          <button key={f} style={{ ...tabBtn(filter === f), flex: '0 0 auto', padding: '5px 10px', fontSize: '10px' }}
            onClick={() => setFilter(f)}>
            {f === 'all' ? `All (${strategies.length})` : f}
          </button>
        ))}
      </div>
      {shown.map(s => (
        <div key={s.id} style={{ ...card(), marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', flex: 1, marginRight: '8px' }}>{s.title}</div>
            {sbadge(s.status)}
          </div>
          <div style={{ fontSize: '11px', color: C.muted, marginBottom: '8px', lineHeight: '1.5' }}>{s.description}</div>
          {s.conditions?.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              {s.conditions.slice(0, 4).map((c, i) => (
                <div key={i} style={{ fontSize: '11px', color: C.blue }}>✓ {c}</div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: C.muted }}>
            <span>Trades: <strong style={{ color: C.text }}>{s.total_trades}</strong></span>
            <span>WR: <strong style={{ color: s.win_rate >= 60 ? C.green : C.gold }}>{s.win_rate}%</strong></span>
            <span style={{ fontSize: '10px', color: C.muted }}>Source: {s.source || 'JARVIS'}</span>
          </div>
        </div>
      ))}
    </>
  );
}

// ── Hypotheses Tab ─────────────────────────────────────────────────────────
function HypothesesTab({ hypotheses }) {
  if (!hypotheses.length) {
    return (
      <div style={{ ...card(), textAlign: 'center', padding: '30px', color: C.muted, fontSize: '13px' }}>
        <div style={{ fontSize: '32px', marginBottom: '10px' }}>💡</div>
        Brain Analysis नंतर 5+ trades असल्यास JARVIS IF-THEN hypotheses propose करेल.
      </div>
    );
  }
  return hypotheses.map(h => (
    <div key={h.id} style={{ ...card(), marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', flex: 1, marginRight: '8px', lineHeight: '1.4' }}>{h.statement}</div>
        <span style={{ background: 'rgba(0,212,255,0.15)', color: C.blue, borderRadius: '4px', padding: '2px 7px', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>
          {h.confidence}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: C.muted }}>
        <span>Confidence: <strong style={{ color: C.text }}>{h.jarvis_confidence_pct}%</strong></span>
        <span>Tested: <strong style={{ color: C.text }}>{h.trades_tested}</strong></span>
        <span>✓ {h.confirmed_count} &nbsp; ✗ {h.disproven_count}</span>
      </div>
    </div>
  ));
}

// ── Backup Tab ────────────────────────────────────────────────────────────
const BACKUP_PATH_KEY = 'jarvis_backup_folder_path';
const BACKUP_LAST_KEY = 'jarvis_backup_last_time';

function getAllData() {
  const all = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    try { all[k] = JSON.parse(localStorage.getItem(k)); }
    catch { all[k] = localStorage.getItem(k); }
  }
  return all;
}

function BackupTab() {
  const isElectron = !!window.electronAPI?.isElectron;
  const [folderPath, setFolderPath] = useState(() => localStorage.getItem(BACKUP_PATH_KEY) || '');
  const [lastBackup, setLastBackup]  = useState(() => localStorage.getItem(BACKUP_LAST_KEY) || '');
  const [status,    setStatus]       = useState('');
  const [autoOn,    setAutoOn]       = useState(() => localStorage.getItem('jarvis_auto_backup') === 'true');
  const autoRef = useRef(null);

  // Perform backup
  const doBackup = useCallback(async (silent = false) => {
    if (!folderPath) { setStatus('❌ Backup folder निवडा आधी.'); return; }

    const data     = getAllData();
    const today    = new Date().toISOString().slice(0, 10);
    const filename = `JARVIS_backup_${today}.json`;
    const content  = JSON.stringify(data, null, 2);

    if (isElectron) {
      const result = await window.electronAPI.backupWrite(folderPath, filename, content);
      if (result.success) {
        const now = new Date().toLocaleString('en-IN');
        setLastBackup(now);
        localStorage.setItem(BACKUP_LAST_KEY, now);
        if (!silent) setStatus(`✅ Backup saved: ${result.fullPath}`);
      } else {
        if (!silent) setStatus(`❌ Backup failed: ${result.error}`);
      }
    } else {
      // Browser fallback — download as file
      const blob = new Blob([content], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      const now = new Date().toLocaleString('en-IN');
      setLastBackup(now);
      localStorage.setItem(BACKUP_LAST_KEY, now);
      if (!silent) setStatus('✅ Backup downloaded!');
    }
  }, [folderPath, isElectron]);

  // Auto-backup: every 24 hours
  useEffect(() => {
    if (!autoOn || !folderPath) { clearInterval(autoRef.current); return; }
    autoRef.current = setInterval(() => { doBackup(true); }, 24 * 60 * 60 * 1000);
    return () => clearInterval(autoRef.current);
  }, [autoOn, folderPath, doBackup]);

  const chooseFolder = async () => {
    if (!isElectron) { setStatus('⚠️ Folder choose — Electron app मध्ये काम करतो.'); return; }
    const result = await window.electronAPI.backupChooseFolder();
    if (result.success) {
      setFolderPath(result.folderPath);
      localStorage.setItem(BACKUP_PATH_KEY, result.folderPath);
      setStatus(`✅ Folder set: ${result.folderPath}`);
    }
  };

  const toggleAuto = () => {
    const next = !autoOn;
    setAutoOn(next);
    localStorage.setItem('jarvis_auto_backup', String(next));
    setStatus(next ? '✅ Auto-backup ON — दर 24 तासांनी backup होईल' : '⏸ Auto-backup OFF');
  };

  // What's in the backup
  const rules     = getRules().length;
  const chunks    = getKnowledgeChunks().length;
  const trades    = getTrades().length;
  const strategies = getStrategies().length;

  return (
    <div>
      {/* Backup location */}
      <div style={card()}>
        <div style={{ fontSize: '10px', color: C.blue, letterSpacing: '2px', marginBottom: '12px' }}>📁 BACKUP LOCATION</div>

        {folderPath ? (
          <div style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
            <div style={{ fontSize: '10px', color: C.muted, marginBottom: '2px' }}>CURRENT BACKUP FOLDER</div>
            <div style={{ fontSize: '12px', color: C.green, wordBreak: 'break-all' }}>{folderPath}</div>
          </div>
        ) : (
          <div style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.2)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px', fontSize: '12px', color: C.gold }}>
            ⚠️ अजून folder निवडला नाही. खाली "Choose Folder" click करा.
          </div>
        )}

        <button style={{ ...btn(C.blue), width: '100%', padding: '10px', marginBottom: '8px' }} onClick={chooseFolder}>
          📁 Choose Backup Folder
        </button>
      </div>

      {/* What's in the backup */}
      <div style={{ ...card(), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1px', gridColumn: 'span 2', marginBottom: '4px' }}>
          BACKUP MADHE WHAT'S INCLUDED
        </div>
        {[
          { label: 'Knowledge Chunks', value: chunks, color: C.blue  },
          { label: 'Rules',            value: rules,  color: C.green },
          { label: 'Strategies',       value: strategies, color: C.purple },
          { label: 'Trades',           value: trades, color: C.gold  },
        ].map(item => (
          <div key={item.label} style={{ background: `${item.color}08`, border: `1px solid ${item.color}22`, borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: '800', color: item.color }}>{item.value}</div>
            <div style={{ fontSize: '10px', color: C.muted }}>{item.label}</div>
          </div>
        ))}
        <div style={{ gridColumn: 'span 2', fontSize: '11px', color: C.muted }}>
          + सगळे behaviors, psychology logs, predictions, lessons — सगळे local data backup होतो
        </div>
      </div>

      {/* Auto backup toggle */}
      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: C.text }}>🔄 Auto Backup (Daily)</div>
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>दर 24 तासांनी automatic backup</div>
          </div>
          <button
            style={{ ...btn(autoOn ? C.green : C.muted), padding: '6px 16px', fontSize: '12px' }}
            onClick={toggleAuto}
          >
            {autoOn ? '✅ ON' : '⏸ OFF'}
          </button>
        </div>

        {lastBackup && (
          <div style={{ fontSize: '11px', color: C.muted }}>
            🕐 Last backup: <span style={{ color: C.text }}>{lastBackup}</span>
          </div>
        )}
      </div>

      {/* Manual backup */}
      <button
        style={{ ...btn(C.gold), width: '100%', padding: '12px', fontSize: '13px', marginBottom: '8px' }}
        onClick={() => doBackup(false)}
        disabled={!folderPath && isElectron}
      >
        💾 Backup Now
      </button>

      {status && (
        <div style={{
          padding: '10px 12px', borderRadius: '8px', fontSize: '12px',
          background: status.startsWith('✅') ? 'rgba(0,255,136,0.08)' : 'rgba(255,215,0,0.08)',
          border: `1px solid ${status.startsWith('✅') ? 'rgba(0,255,136,0.25)' : 'rgba(255,215,0,0.2)'}`,
          color: status.startsWith('✅') ? C.green : C.gold,
        }}>
          {status}
        </div>
      )}

      {/* Restore info */}
      <div style={{ ...card({ marginTop: '12px' }), background: 'rgba(167,139,250,0.06)', borderColor: 'rgba(167,139,250,0.2)' }}>
        <div style={{ fontSize: '10px', color: C.purple, letterSpacing: '2px', marginBottom: '8px' }}>🔁 RESTORE BACKUP</div>
        <div style={{ fontSize: '12px', color: C.muted, lineHeight: '1.7' }}>
          Computer problem झाल्यास:<br />
          1. JARVIS app open करा<br />
          2. Knowledge Base → JSON Upload → backup file upload करा<br />
          3. किंवा Settings मध्ये "Restore from Backup" (येणार)
        </div>
      </div>
    </div>
  );
}

// ── Weekly Briefing Tab ────────────────────────────────────────────────────
function BriefingTab({ memory, onGenerate, isGenerating }) {
  const briefings = (memory.weekly_briefings || []).slice().reverse();
  const [expanded, setExpanded] = useState(null);
  return (
    <>
      <div style={{ ...card({ background: 'rgba(255,215,0,0.06)' }), padding: '14px' }}>
        <div style={{ fontSize: '12px', color: C.muted, marginBottom: '12px', lineHeight: '1.6' }}>
          JARVIS past 7 days analyze करतो — lessons, trades, patterns — आणि next week चे focus areas देतो.
        </div>
        <button style={{ ...btn(C.gold), width: '100%', padding: '11px', fontSize: '13px', opacity: isGenerating ? 0.6 : 1 }}
          onClick={onGenerate} disabled={isGenerating}>
          {isGenerating ? '⏳ Generating...' : '📋 Generate This Week\'s Briefing'}
        </button>
      </div>
      {briefings.map((b, i) => (
        <div key={i} style={{ ...card(), marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setExpanded(expanded === i ? null : i)}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '600' }}>
                Week of {b.week_start || new Date(b.generated_at).toLocaleDateString('en-IN')}
              </div>
              <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                {b.summary?.slice(0, 80)}...
              </div>
            </div>
            <span style={{ color: C.muted, fontSize: '16px' }}>{expanded === i ? '▲' : '▼'}</span>
          </div>
          {expanded === i && (
            <div style={{ marginTop: '12px', borderTop: `1px solid ${C.border}`, paddingTop: '12px' }}>
              {b.summary && <div style={{ fontSize: '12px', color: C.text, lineHeight: '1.6', marginBottom: '8px' }}>{b.summary}</div>}
              {b.key_lessons?.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '10px', color: C.muted, marginBottom: '4px' }}>KEY LESSONS</div>
                  {b.key_lessons.map((l, j) => <div key={j} style={{ fontSize: '12px', color: C.muted, marginBottom: '3px' }}>• {l}</div>)}
                </div>
              )}
              {b.next_week_focus?.length > 0 && (
                <div>
                  <div style={{ fontSize: '10px', color: C.muted, marginBottom: '4px' }}>NEXT WEEK FOCUS</div>
                  {b.next_week_focus.map((f, j) => <div key={j} style={{ fontSize: '12px', color: C.blue, marginBottom: '3px' }}>→ {f}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

// ── Patterns (Self-Learning) ─────────────────────────────────────────────
// Real statistics over JARVIS's own real trade history — never a claim
// without the raw numbers next to it, never enough to auto-restrict a
// trade. See src/engines/selfLearning.js for the full discipline this
// follows (minimum 15-trade sample per bucket, 15-point minimum gap).
function PatternsTab() {
  const [result, setResult] = useState(() => computePatterns());
  const [addedIds, setAddedIds] = useState(() => new Set());

  const refresh = () => setResult(computePatterns());

  const handleSuggestRule = (finding) => {
    saveRule({
      title: `Auto-detected: ${finding.label}`,
      condition: `Trade matches "${finding.label}"`,
      action: finding.better ? 'FAVOR' : 'CAUTION',
      source: 'Self-Learning (auto-detected)',
      notes: `${finding.wins}/${finding.total} trades won (${finding.winRate}%) vs ${finding.baselineRate}% overall baseline — ${finding.better ? 'performs better' : 'performs worse'} than average. Sample size ${finding.total}. This is a statistical observation, not a proven rule — review before relying on it.`,
    });
    setAddedIds((prev) => new Set(prev).add(finding.label));
  };

  return (
    <>
      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>🔬 SELF-LEARNING PATTERNS</div>
          <button style={btn(C.blue)} onClick={refresh}>🔄 पुन्हा तपासा</button>
        </div>
        <div style={{ fontSize: '11px', color: C.muted, lineHeight: '1.6' }}>
          फक्त JARVIS ने स्वतः शोधलेल्या (auto-logged) trades वरून — मॅन्युअली टाकलेले
          trades मोजले जात नाहीत, कारण त्यांना signal context नसतो. किमान {MIN_SAMPLE_SIZE}
          बंद trades नसतील त्या गटासाठी, तर काहीच दाखवलं जात नाही — अंदाज नाही.
        </div>
      </div>

      {!result.ready ? (
        <div style={{ ...card(), textAlign: 'center', padding: '30px 16px' }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>📊</div>
          <div style={{ fontSize: '13px', color: C.text, marginBottom: '6px' }}>अजून पुरेसा data नाही</div>
          <div style={{ fontSize: '12px', color: C.muted }}>
            {result.contextTrades} / {result.needed} JARVIS-logged closed trades जमलेत — पहिला
            pattern दिसण्यासाठी किमान {result.needed} लागतील.
          </div>
        </div>
      ) : (
        <>
          <div style={{ ...card(), display: 'flex', gap: '20px' }}>
            <Stat label="CONTEXT TRADES" value={result.contextTrades} color={C.blue} />
            <Stat label="BASELINE WIN RATE" value={`${result.baselineWinRate}%`} color={C.gold} />
            <Stat label="FINDINGS" value={result.findings.length} color={result.findings.length ? C.purple : C.muted} />
          </div>

          {result.findings.length === 0 ? (
            <div style={{ ...card(), textAlign: 'center', padding: '24px 16px', color: C.muted, fontSize: '13px' }}>
              अजून कुठलाही ठळक pattern सापडलेला नाही — म्हणजे सगळे setups साधारण सारखेच
              चालतायत, हेही एक चांगली गोष्ट आहे.
            </div>
          ) : (
            result.findings.map((f) => (
              <div key={f.label} style={{ ...card(), borderLeft: `3px solid ${f.better ? C.green : C.red}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>
                      {f.label} — {f.better ? 'बरं चाललंय' : 'सांभाळून'}
                    </div>
                    <div style={{ fontSize: '12px', color: f.better ? C.green : C.red, marginTop: '4px', fontWeight: '600' }}>
                      {f.winRate}% win rate ({f.wins}/{f.total}) — baseline {f.baselineRate}% पेक्षा {Math.abs(f.gap)} points {f.better ? 'जास्त' : 'कमी'}
                    </div>
                  </div>
                  <button
                    style={{ ...btn(addedIds.has(f.label) ? C.green : C.purple), flexShrink: 0, fontSize: '11px' }}
                    onClick={() => handleSuggestRule(f)}
                    disabled={addedIds.has(f.label)}
                  >
                    {addedIds.has(f.label) ? '✓ जोडलं' : 'Rule Book मध्ये सुचना जोडा'}
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function JarvisMemory() {
  const { runBrainAnalysis, runWeeklyBriefing, isThinking } = useJarvis();
  const [tab, setTab]       = useState('overview');
  const [brainRunning, setBrainRunning] = useState(false);
  const [brainResult,  setBrainResult]  = useState(null);

  const memory     = getJarvisMemoryData();
  const report     = getBrainReport();
  const strategies = getStrategies();
  const hypotheses = getHypotheses();

  const tabs = [
    { id: 'overview',   label: '📊 Overview'   },
    { id: 'brain',      label: '🧬 Brain'      },
    { id: 'strategies', label: '🎯 Strategies' },
    { id: 'patterns',   label: '🔬 Patterns'   },
    { id: 'hypotheses', label: '💡 Hyp'        },
    { id: 'backup',     label: '💾 Backup'     },
    { id: 'briefing',   label: '📋 Briefing'   },
  ];

  const handleRunBrain = useCallback(() => {
    setBrainRunning(true);
    setBrainResult(null);
    setTimeout(() => {
      const result = runBrainAnalysis();
      setBrainResult(result);
      setBrainRunning(false);
    }, 800);
  }, [runBrainAnalysis]);

  const handleGenerateBriefing = useCallback(async () => {
    const trades  = getTrades();
    const lessons = getDailyLessons().slice(-7);
    const closed  = trades.filter(t => t.result && t.result !== 'OPEN');
    const wins    = closed.filter(t => t.result === 'WIN').length;
    await runWeeklyBriefing({
      week_start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA'),
      week_end:   new Date().toLocaleDateString('en-CA'),
      trades_taken: closed.slice(-20),
      total_trades: closed.length,
      win_rate:     closed.length ? Math.round((wins / closed.length) * 100) : 0,
      total_pnl:    closed.reduce((s, t) => s + (parseFloat(t.pnl_usd) || 0), 0).toFixed(2),
      daily_lessons: lessons,
    });
  }, [runWeeklyBriefing]);

  return (
    <div style={{ padding: '16px', color: C.text, fontFamily: 'monospace', maxWidth: '100%' }}>
      <div style={{ fontSize: '18px', fontWeight: '700', color: C.gold, marginBottom: '2px', letterSpacing: '2px' }}>
        🧬 JARVIS MEMORY
      </div>
      <div style={{ fontSize: '11px', color: C.muted, marginBottom: '16px', letterSpacing: '1px' }}>
        COMPLETE BRAIN STATUS · BACKUP · STRATEGIES · EVOLUTION
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '5px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...tabBtn(tab === t.id), flex: '0 0 auto', padding: '6px 10px' }}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview'   && <MemoryOverview />}
      {tab === 'brain'      && <BrainTab memory={memory} report={report} onRunBrain={handleRunBrain} isRunning={brainRunning} brainResult={brainResult} />}
      {tab === 'strategies' && <StrategiesTab strategies={strategies} />}
      {tab === 'patterns'   && <PatternsTab />}
      {tab === 'hypotheses' && <HypothesesTab hypotheses={hypotheses} />}
      {tab === 'backup'     && <BackupTab />}
      {tab === 'briefing'   && <BriefingTab memory={memory} onGenerate={handleGenerateBriefing} isGenerating={isThinking} />}
    </div>
  );
}
